import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/AuthContext";
import PushService from "../../../services/PushService";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";

import LayoutOnboarding from "../../../components/layout/layoutOnboarding";
import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";

import { ButtonGradient } from "../../../components/theme/Button";
import {
  D1,
  GradientSpan,
  H1,
  P3,
} from "../../../components/theme/text/Headings";
import {
  Blue,
  Pink,
  OffWhite,
  Purple,
  White,
  gradientColors,
} from "../../../components/theme/colors";

const TopContent = styled.div`
  margin-top: 6vh;
  padding: 0 15px;
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 100px;
  left: 2vh;
  right: 2vh;
`;

const OnboardingNotifications = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();
  const [permission, setPermission] = useState("prompt");
  const [working, setWorking] = useState(false);

  const refreshPermission = useCallback(async () => {
    try {
      const status = await PushService.checkPermissionStatus();
      setPermission(status || "prompt");
    } catch (_) {
      setPermission("denied");
    }
  }, []);

  useEffect(() => {
    refreshPermission();
  }, [refreshPermission]);

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "notifications",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Notifications Viewed", props);
    postHogService.track("Onboarding Notifications Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAllow = async () => {
    if (!currentUserState) return;
    setWorking(true);
    try {
      const granted = await PushService.requestAndRegister(currentUserState);
      if (granted) {
        setPermission("granted");
      } else {
        setPermission("denied");
      }
    } catch (_) {
      setPermission("denied");
    } finally {
      setWorking(false);
      navigate("/pro", { state: { fromOnboarding: true } });
    }
  };

  const handleSkip = () => {
    navigate("/pro", { state: { fromOnboarding: true } });
  };

  const handleContinue = () => {
    navigate("/pro", { state: { fromOnboarding: true } });
  };

  const isGranted = permission === "granted";

  return (
    <LayoutOnboarding>
      <ProgressBarContainer>
        <ProgressBarProgress
          $background={Blue}
          $transitionBackground={Purple}
          width={`100%`}
        />
      </ProgressBarContainer>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <D1 color={White}>
                Never{" "}
                <GradientSpan $background={Blue} $transitionBackground={Pink}>
                  miss a vibe.
                </GradientSpan>
              </D1>
              <H1 color={White} className="mt-40 mb-40">
                Enable notifications to get instant updates on results and
                friend activity.
              </H1>
            </TopContent>
            <BottomLogin>
              <div style={{ padding: "0 15px" }}>
                {isGranted ? (
                  <ButtonGradient
                    width="100%"
                    className="mt-20"
                    $background={gradientColors["personality-check"].background}
                    $transitionBackground={
                      gradientColors["personality-check"].transitionBackground
                    }
                    onClick={handleContinue}
                  >
                    Continue
                  </ButtonGradient>
                ) : (
                  <ButtonGradient
                    width="100%"
                    className="mt-20"
                    $background={gradientColors["personality-check"].background}
                    $transitionBackground={
                      gradientColors["personality-check"].transitionBackground
                    }
                    onClick={handleAllow}
                    disabled={working}
                  >
                    {working ? "Enabling..." : "Allow Notifications"}
                  </ButtonGradient>
                )}
                {!isGranted && (
                  <div onClick={handleSkip}>
                    <P3 color={OffWhite} className="mt-20">
                      Maybe later
                    </P3>
                  </div>
                )}
              </div>
            </BottomLogin>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingNotifications;
